import {
    ChevronRight,
    Ear,
    type LucideIcon,
    PhoneForwarded,
    PhoneIncoming,
    PhoneMissed,
    PhoneOff,
    PhoneOutgoing,
    Sparkles,
    Voicemail,
} from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import {
    buildPlainSpeakerTranscriptRows,
    buildStructuredTranscriptRows,
    formatStructuredSotTranscriptText,
    getSpeakerLabelsFromTranscriptText,
    normalizeCallTranscriptText,
    renderTranscriptWithSpeakerLabels,
    type TranscriptTurnRow,
    TranscriptTurnTimestamp,
} from '@apps/web/components/voice_calls/call_transcript_display_utils';
import { type SotTranscriptTurnDisplay } from '@apps/web/components/voice_calls/sot_transcript_display';
import { VoiceCallAudioPlayer } from '@apps/web/components/voice_calls/voice_call_audio_player';
import {
    formatCallDisplayStatusLabel,
    formatCallDurationLabel,
    isVoiceCallListeningOnly,
} from '@apps/web/components/voice_calls/voice_call_format_utils';
import { hasVoiceCallVoicemailBadge } from '@apps/web/components/voice_calls/voice_call_voicemail_utils';
import { formatNationalPhoneNumber } from '@apps/web/lib/contact_utils';
import { cn } from '@apps/web/lib/utils';
import { type VoiceCall } from '@libraries/typescript/api_client';

type VoiceCallTranscriptSotAuditLike = {
    readonly reference_transcript: string | null;
    readonly reference_transcript_turns?: ReadonlyArray<SotTranscriptTurnDisplay>;
    readonly post_transfer_start_ms?: number | null;
};

type VoiceCallTranscriptCardCall = {
    readonly direction: string;
    readonly display_status: string;
    readonly transferred_to_back_office: boolean;
    readonly transfer_number?: string | null;
    readonly transfer_destination_label?: string | null;
    readonly transfer_outcome_summary?: string | null;
    readonly attempted_transfer_number?: string | null;
    readonly attempted_transfer_destination_label?: string | null;
    readonly voicemail_detected?: boolean | null;
    readonly post_transfer_audio_outcome?: string | null;
    readonly recording_url: string | null;
    readonly call_summary: string | null;
    readonly duration_ms?: number | null;
    readonly routing_reason?: string | null;
    readonly transcript_sot_audit?: VoiceCallTranscriptSotAuditLike | null;
};

type VoiceCallTranslationLike = {
    readonly source_language_name: string;
    readonly translated_text: string;
};

export type CallIconTone = 'danger' | 'success' | 'info' | 'muted';

const callIconClassNames = {
    danger: 'bg-red-600 text-white',
    success: 'bg-emerald-600 text-white',
    info: 'bg-blue-600 text-white',
    muted: 'bg-slate-700 text-white',
} satisfies Record<CallIconTone, string>;

function getCallIconClassName(tone: CallIconTone) {
    return callIconClassNames[tone];
}

function formatTransferLabel(
    destinationLabel: string | null | undefined,
): string {
    return destinationLabel
        ? `Transferred to ${destinationLabel}`
        : 'Transferred';
}

export function getVoiceCallTranscriptPresentation(
    call: VoiceCallTranscriptCardCall,
    silentListeningLabelSettingsEnabled: boolean = false,
): {
    readonly title: string;
    readonly subtitle: string;
    readonly Icon: LucideIcon;
    readonly iconClassName: string;
    readonly tone: CallIconTone;
} {
    // display_status carries the observed outreach outcome, so it is checked
    // before the voicemail-detection heuristics: "Message left" is only
    // claimed when the backend recorded voicemail_left, never merely because
    // a voicemail box was detected.
    if (call.display_status === 'voicemail_left') {
        return {
            title: 'Voicemail',
            subtitle: 'Message left',
            Icon: Voicemail,
            iconClassName: getCallIconClassName('muted'),
            tone: 'muted',
        };
    }

    if (call.display_status === 'unable_to_leave_message') {
        return {
            title: 'Voicemail',
            subtitle: 'Couldn’t leave a message',
            Icon: Voicemail,
            iconClassName: getCallIconClassName('danger'),
            tone: 'danger',
        };
    }

    if (call.display_status === 'no_answer') {
        return {
            title: 'Missed call',
            subtitle: 'No one answered',
            Icon: PhoneMissed,
            iconClassName: getCallIconClassName('danger'),
            tone: 'danger',
        };
    }

    if (
        call.display_status === 'failed' ||
        call.display_status === 'unreachable' ||
        call.display_status === 'call_disconnected'
    ) {
        return {
            title: 'Failed call',
            subtitle: formatCallDisplayStatusLabel(call.display_status),
            Icon: PhoneOff,
            iconClassName: getCallIconClassName('danger'),
            tone: 'danger',
        };
    }

    if (hasVoiceCallVoicemailBadge(call)) {
        return {
            title: 'Voicemail',
            subtitle: 'Reached a voicemail box',
            Icon: Voicemail,
            iconClassName: getCallIconClassName('muted'),
            tone: 'muted',
        };
    }

    if (isVoiceCallListeningOnly(call, silentListeningLabelSettingsEnabled)) {
        return {
            title: 'Listening mode',
            subtitle: 'Phoebe did not answer or speak',
            Icon: Ear,
            iconClassName: getCallIconClassName('info'),
            tone: 'info',
        };
    }

    if (
        call.transferred_to_back_office ||
        call.display_status === 'transferred'
    ) {
        return {
            title: 'Transferred call',
            subtitle: formatTransferLabel(call.transfer_destination_label),
            Icon: PhoneForwarded,
            iconClassName: getCallIconClassName('info'),
            tone: 'info',
        };
    }

    const isInboundCall = call.direction === 'inbound';
    const tone: CallIconTone =
        call.display_status === 'live_answer' ||
        call.display_status === 'completed' ||
        call.display_status === 'ended'
            ? 'success'
            : 'info';
    return {
        title: isInboundCall ? 'Inbound call' : 'Outbound call',
        subtitle: formatCallDisplayStatusLabel(call.display_status),
        Icon: isInboundCall ? PhoneIncoming : PhoneOutgoing,
        iconClassName: getCallIconClassName(tone),
        tone,
    };
}

export type VoiceCallTranscriptViewModel = {
    readonly hasContent: boolean;
    readonly title: string;
    readonly subtitle: string;
    readonly Icon: LucideIcon;
    readonly iconClassName: string;
    readonly timelineLabel: string;
    readonly summaryText: string;
    readonly transcriptText: string | null;
    readonly transcriptLabel: string;
    readonly transcriptRows: ReadonlyArray<TranscriptTurnRow>;
    readonly transferDestinationLabel: string | null;
    // What the transfer dial reached ("answered", "voicemail", "no_answer",
    // …); null when the call never dialed a transfer or no evidence exists.
    readonly transferOutcomeSummary: string | null;
    // Where Phoebe tried to send the call when a transfer was dialed but never
    // connected. Null unless the call had a failed transfer attempt.
    readonly attemptedTransferLabel: string | null;
    readonly translation?: VoiceCallTranslationLike | null;
};

export function buildVoiceCallTranscriptViewModel({
    call,
    timestampLabel,
    transcriptText: rawTranscriptText,
    translation = null,
    silentListeningLabelSettingsEnabled = false,
}: {
    readonly call: VoiceCallTranscriptCardCall;
    readonly timestampLabel: string;
    readonly transcriptText: string | null | undefined;
    readonly translation?: VoiceCallTranslationLike | null;
    readonly silentListeningLabelSettingsEnabled?: boolean;
}): VoiceCallTranscriptViewModel {
    const transcriptSotAudit = call.transcript_sot_audit ?? null;
    const transcriptSotTurns =
        transcriptSotAudit?.reference_transcript_turns ?? [];
    const postTransferSotStartMs =
        transcriptSotAudit?.post_transfer_start_ms ?? null;
    const normalizedSotTranscript =
        normalizeCallTranscriptText(transcriptSotAudit?.reference_transcript) ??
        formatStructuredSotTranscriptText(
            transcriptSotTurns,
            postTransferSotStartMs,
        );
    const transcriptText =
        normalizedSotTranscript ??
        normalizeCallTranscriptText(rawTranscriptText);
    const transcriptSpeakerLabels =
        getSpeakerLabelsFromTranscriptText(transcriptText);
    const structuredTranscriptRows = buildStructuredTranscriptRows({
        postTransferSotStartMs,
        showTransferMarker: call.transferred_to_back_office,
        speakerLabels: transcriptSpeakerLabels,
        turns: transcriptSotTurns,
    });
    const plainSpeakerTranscriptRows = buildPlainSpeakerTranscriptRows({
        durationMs: call.duration_ms,
        transcriptText,
    });
    const transcriptRows =
        structuredTranscriptRows.length > 0
            ? structuredTranscriptRows
            : plainSpeakerTranscriptRows;
    const normalizedCallSummary = normalizeCallTranscriptText(
        call.call_summary,
    );
    const transcriptDurationLabel = formatCallDurationLabel(
        call.duration_ms ?? null,
    );
    const timelineLabel = transcriptDurationLabel
        ? `${timestampLabel} · ${transcriptDurationLabel}`
        : timestampLabel;
    const presentation = getVoiceCallTranscriptPresentation(
        call,
        silentListeningLabelSettingsEnabled,
    );
    const attemptedTransferLabel =
        call.attempted_transfer_destination_label ??
        formatNationalPhoneNumber(call.attempted_transfer_number);

    return {
        hasContent:
            !!call.recording_url ||
            !!normalizedCallSummary ||
            !!transcriptText ||
            transcriptSotTurns.length > 0,
        title: presentation.title,
        subtitle: presentation.subtitle,
        Icon: presentation.Icon,
        iconClassName: presentation.iconClassName,
        timelineLabel,
        summaryText: normalizedCallSummary ?? 'No summary available.',
        transcriptText,
        transcriptLabel: 'Transcript',
        transcriptRows,
        transferDestinationLabel:
            call.transfer_destination_label ??
            formatNationalPhoneNumber(call.transfer_number),
        transferOutcomeSummary: call.transfer_outcome_summary ?? null,
        attemptedTransferLabel,
        translation,
    };
}

export function VoiceCallTranscriptCard({
    bodyFooter,
    className,
    emptyBody,
    footer,
    headerAccessory,
    isRecordingUrlLoading,
    onOpen,
    prefetchedRecordingUrl,
    recordingUrlError,
    viewModel,
    voiceCall,
}: {
    readonly bodyFooter?: ReactNode;
    readonly className?: string;
    readonly emptyBody?: ReactNode;
    readonly footer?: ReactNode;
    readonly headerAccessory?: ReactNode;
    readonly isRecordingUrlLoading?: boolean;
    readonly onOpen?: () => void;
    readonly prefetchedRecordingUrl?: string;
    readonly recordingUrlError?: unknown;
    readonly viewModel: VoiceCallTranscriptViewModel;
    readonly voiceCall: Pick<VoiceCall, 'id' | 'recording_url'>;
}) {
    const Icon = viewModel.Icon;
    const header = (
        <div className="flex items-start gap-3">
            <div
                className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                    viewModel.iconClassName,
                )}
            >
                <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h3 className="text-foreground text-base leading-5 font-semibold">
                            {viewModel.title}
                        </h3>
                        <p className="text-muted-foreground text-sm leading-5">
                            {viewModel.subtitle}
                        </p>
                        <p className="text-muted-foreground mt-1 text-xs font-medium">
                            {viewModel.timelineLabel}
                        </p>
                    </div>
                    {headerAccessory}
                </div>
            </div>
        </div>
    );
    const transcriptContent = viewModel.transcriptText ? (
        <Fragment>
            {viewModel.transcriptRows.length > 0 ? (
                <div className="space-y-2">
                    {viewModel.transcriptRows.map((turn) => (
                        <Fragment key={turn.key}>
                            {turn.isPostTransferStart ? (
                                <div className="flex justify-center">
                                    <Badge
                                        variant="success"
                                        className="max-w-full px-2 py-0.5 text-[10px]"
                                    >
                                        <span className="truncate">
                                            {viewModel.transferDestinationLabel
                                                ? formatTransferLabel(
                                                      viewModel.transferDestinationLabel,
                                                  )
                                                : 'Transferred to back office'}
                                        </span>
                                    </Badge>
                                </div>
                            ) : null}
                            <div className="grid gap-2 text-sm leading-6 sm:grid-cols-[minmax(0,1fr)_3.25rem]">
                                <p className="text-foreground min-w-0 break-words">
                                    <strong className="text-foreground font-semibold">
                                        {`${turn.speakerLabel}:`}
                                    </strong>{' '}
                                    {turn.text}
                                </p>
                                <TranscriptTurnTimestamp turn={turn} />
                            </div>
                        </Fragment>
                    ))}
                </div>
            ) : (
                <p className="text-foreground text-sm leading-6 break-words whitespace-pre-wrap">
                    {renderTranscriptWithSpeakerLabels(
                        viewModel.transcriptText,
                    )}
                </p>
            )}
            {viewModel.translation ? (
                <div className="border-border/60 border-t pt-2">
                    <div className="text-muted-foreground mb-1 text-[11px] font-medium tracking-wide uppercase">
                        Translated from{' '}
                        {viewModel.translation.source_language_name}
                    </div>
                    <p className="text-foreground text-sm leading-6 break-words whitespace-pre-wrap">
                        {renderTranscriptWithSpeakerLabels(
                            viewModel.translation.translated_text,
                        )}
                    </p>
                </div>
            ) : null}
        </Fragment>
    ) : null;

    return (
        <div
            className={cn(
                'bg-muted ring-border/80 overflow-hidden rounded-2xl shadow-sm ring-1',
                className,
            )}
        >
            {onOpen ? (
                <button
                    className="hover:bg-accent w-full px-4 pt-4 pb-3 text-left transition-colors"
                    onClick={onOpen}
                    type="button"
                >
                    {header}
                </button>
            ) : (
                <div className="px-4 pt-4 pb-3">{header}</div>
            )}
            {viewModel.hasContent ? (
                <div className="px-4 pb-3">
                    <div className="ml-[3.25rem] space-y-3">
                        <div className="border-border/80 bg-background/70 rounded-xl border px-3 py-2.5">
                            <div className="text-muted-foreground mb-1.5 flex items-center gap-1.5 text-xs font-semibold">
                                <Sparkles className="h-3.5 w-3.5" />
                                <span>Call summary</span>
                            </div>
                            <p className="text-foreground text-sm leading-6 break-words whitespace-pre-wrap">
                                {viewModel.summaryText}
                            </p>
                        </div>
                        {viewModel.transcriptText ? (
                            <Collapsible key={voiceCall.id}>
                                <CollapsibleTrigger className="group text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-sm font-medium">
                                    <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90" />
                                    <span>{viewModel.transcriptLabel}</span>
                                </CollapsibleTrigger>
                                <CollapsibleContent className="pt-2">
                                    {transcriptContent}
                                </CollapsibleContent>
                            </Collapsible>
                        ) : null}
                    </div>
                </div>
            ) : (
                emptyBody
            )}
            {bodyFooter}
            {voiceCall.recording_url ? (
                <div className="px-4 pb-4">
                    <div className="bg-muted/80 rounded-xl px-2 py-1.5">
                        <VoiceCallAudioPlayer
                            className="h-9 w-full focus:outline-none"
                            isRecordingUrlLoading={isRecordingUrlLoading}
                            prefetchedRecordingUrl={prefetchedRecordingUrl}
                            recordingUrlError={recordingUrlError}
                            voiceCall={voiceCall}
                        />
                    </div>
                </div>
            ) : null}
            {footer}
        </div>
    );
}
